import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
import { PAGE_CURSOR_MAX, pagedQueryFields } from "@zion/sdk-contracts";
//#region src/access-review.ts
/**
* `/v1/admin/access-review`: the DP access review of plan `2026-10-22-dp-admin-insights` (issue
* #4487, S1). An auditor of one org asks two questions per person: which apps can this person see,
* and did the person open them. One read answers both, a page of people at a time.
*
* Producer: core-api's access-review controller in `cloud/dp/core-api/src/access-review/` (WS-1).
* Consumers: the org admin app in `witness-ai/zion-admin-app`, page "Access review" (WS-3), and
* core-api's `admin-route-census.spec.ts`, which checks the route against `openapi.json`.
*
* Authorization (D-15). The route carries `@RequireOrgPermission('access-review:read')` and
* `@RequiresKernelRight('audit.read', 0)`; its kernel class in `CONTRACT_ROUTE_KERNEL_CLASS` is
* `audit.read` tier 0, the class of `GET /v1/admin/audit-log`. The org comes from the
* authenticated principal only. No field of the query names an org, and the strict query schema
* refuses an `orgId` key with 400. Every read runs under the caller's org through RLS.
*
* The window rules that core-api applies, in UTC days (overview "Definitions and window rules"):
*
* - `opens30d` counts the opens of the last {@link ACCESS_REVIEW_OPENS_WINDOW_DAYS} days.
* - `unused` looks back {@link ACCESS_REVIEW_UNUSED_WINDOW_DAYS} days, and is `null` until that
*   much history covers both the person and the org.
* - `dormant` is one of {@link ACCESS_REVIEW_DORMANT_DAYS}.
*
* Direction rule (R-28 of this package). The query schema is `z.strictObject` and refuses an
* unknown key (index defaults F-1, F-3). Each response schema is `z.object` and strips one. No
* schema here carries a `.transform()` or a `.default()`, so `createOpenApiDocument()` can render
* each one. The page size default is the service's, from {@link ACCESS_REVIEW_PAGE_SIZE_DEFAULT},
* as `pagedQueryFields` of `@zion/sdk-contracts` rules (R-26 of that module). This module does no
* I/O and holds no state.
*/
/** The people list path. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_ACCESS_REVIEW_PEOPLE_PATH = `${ADMIN_API_PREFIX}/access-review/people`;
/**
* `GET /v1/admin/access-review/people`. Query {@link accessReviewPeopleQuerySchema}. Status 200 with
* {@link accessReviewPeopleResponseSchema}: one item per active account of the caller's org
* (`auth.users.status = 'active'`), a page at a time, at most `limit` items. With `appId`, the page
* holds only the people who can see that app, and each person's `apps` holds only that app. A
* removed or deactivated account never appears. Machine principals have no account and never
* appear.
*
* Refusals:
* - 400 for a query that fails the schema: an unknown key, a `limit` outside 1 to 50, an `appId`
*   that is not a UUID, or a `cursor` outside the base64url grammar;
* - 400 for a cursor that core-api cannot decode;
* - 401 with no session;
* - 403 without `access-review:read` or without the kernel right `audit.read`.
*
* The route writes nothing. Kernel class `audit.read` tier 0.
*/
const ACCESS_REVIEW_PEOPLE_ROUTE = route("GET", ADMIN_ACCESS_REVIEW_PEOPLE_PATH);
/**
* The access-review family, in the order `route-census.ts` folds it into `ADMIN_CONTRACT_ROUTES`:
* the one people read. Frozen.
*/
const accessReviewRoutes = Object.freeze([ACCESS_REVIEW_PEOPLE_ROUTE]);
/** The page size core-api applies when the query carries no `limit` (D-14). */
const ACCESS_REVIEW_PAGE_SIZE_DEFAULT = 25;
/** The largest `limit` the query admits (D-14). A larger value is 400, never a clamp. */
const ACCESS_REVIEW_PAGE_SIZE_MAX = 50;
/** The window of `opens30d`, in UTC days, today included. */
const ACCESS_REVIEW_OPENS_WINDOW_DAYS = 30;
/**
* The window of `unused`, in UTC days. It is also the history that must cover a person and the org
* before core-api answers `unused` with a boolean. Until then, the admin app shows "not enough
* history until" `countedSince` plus this many days.
*/
const ACCESS_REVIEW_UNUSED_WINDOW_DAYS = 90;
/**
* The two dormant thresholds, in days (D-9). The reference is the last activity of a person, or
* `createdAt` when the person has none.
* - `90`: the reference is older than 90 days.
* - `30`: else, the reference is older than 30 days.
* - `null`: else; the person is not dormant.
*/
const ACCESS_REVIEW_DORMANT_DAYS = [30, 90];
/**
* The query of {@link ACCESS_REVIEW_PEOPLE_ROUTE}: `{ cursor?, limit?, appId? }`.
*
* - `cursor`: the previous page's `nextCursor`, unchanged. 1 to `PAGE_CURSOR_MAX` (512) characters
*   of base64url with no padding. Opaque: core-api mints it, and it carries no authorization input.
*   core-api answers 400 for a cursor of this grammar that it cannot decode.
* - `limit`: the page size, an integer 1 to {@link ACCESS_REVIEW_PAGE_SIZE_MAX}. A query string
*   carries `"25"`, so the schema coerces it. Absent, core-api applies
*   {@link ACCESS_REVIEW_PAGE_SIZE_DEFAULT}; the schema carries no `.default()`.
* - `appId`: a UUID. It narrows the page to the people who can see that app.
*
* Any other key fails the parse, an `orgId` included, so the caller cannot name another org.
*/
const accessReviewPeopleQuerySchema = z.strictObject({
	...pagedQueryFields({ maxLimit: 50 }),
	appId: z.uuid().optional()
});
/**
* The arm of `filterVisibleApps` (core-api `app-visibility.service.ts`) that kept an app for a
* person, as `explainVisibleApps` reports it (D-12, index defaults F-14):
*
* - `owner`: the person holds the `owner` role;
* - `all-apps-role`: a role of the person opens every app;
* - `first-party`: the app is a first-party app that every member sees;
* - `grant`: an app permission grant to the person, a group of the person or a role of the person;
* - `flat-permission`: an org permission of the person admits the app.
*/
const ACCESS_ARMS = [
	"owner",
	"all-apps-role",
	"first-party",
	"grant",
	"flat-permission"
];
const accessArmSchema = z.enum(ACCESS_ARMS);
/**
* The kinds of path that give a person an app permission key (2026-10-29-app-permission-subjects,
* D-14), in the order a `sources` list sorts them:
*
* - `role`: a role of the person holds the key;
* - `group`: a group holds the key, and the person is in it or in one of its child groups;
* - `user`: a direct grant to the person holds the key.
*/
const ACCESS_REVIEW_GRANT_SOURCE_KINDS = [
	"role",
	"group",
	"user"
];
const accessReviewGrantSourceKindSchema = z.enum(ACCESS_REVIEW_GRANT_SOURCE_KINDS);
/**
* The most groups a group path holds. core-api walks a person's groups up from each direct group
* while the depth is under 8 (`flattenedGroupIdsForPrincipal`), so a path holds 1 to 8 groups.
*/
const ACCESS_REVIEW_GROUP_PATH_MAX = 8;
/** One group on a group path: the group's id and its name. */
const accessReviewGroupRefSchema = z.object({
	id: z.uuid(),
	name: z.string()
});
/**
* One path that gives a person an app permission key. `kind` selects the shape:
*
* - `role`: `id` is the role slug, the form of {@link AccessReviewPerson.roles}; `name` is the
*   role's display name.
* - `group`: `id` and `name` are the group that holds the grant. `groupPath` runs from a direct
*   group of the person up to that group, both ends included, so its last entry has the source's
*   `id` (compared without letter case). No group appears twice on a path: the walk never revisits
*   a group. A direct member gets a path of one entry. When the person reaches the group by more than
*   one path, core-api sends one source for the group with the shortest path; on a tie, the path
*   whose group ids, compared in order, sort first.
* - `user`: `id` is the person's `userId`; `name` is the person's display name, or the email when
*   the account has none.
*/
const accessReviewGrantSourceSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("role"),
		id: z.string().min(1),
		name: z.string()
	}),
	z.object({
		kind: z.literal("group"),
		id: z.uuid(),
		name: z.string(),
		groupPath: z.array(accessReviewGroupRefSchema).min(1).max(8)
	}).refine((source) => source.groupPath[source.groupPath.length - 1]?.id.toLowerCase() === source.id.toLowerCase(), {
		message: "the last group of groupPath must be the group that holds the grant",
		path: ["groupPath"]
	}).refine((source) => new Set(source.groupPath.map((group) => group.id.toLowerCase())).size === source.groupPath.length, {
		message: "a group appears at most once on groupPath",
		path: ["groupPath"]
	}),
	z.object({
		kind: z.literal("user"),
		id: z.uuid(),
		name: z.string()
	})
]);
/**
* One app permission key that a person holds for one app, with every source of the key. `sources`
* sorts by kind in the order of {@link ACCESS_REVIEW_GRANT_SOURCE_KINDS}, then by `id`. A key that
* two paths give lists both, so a reviewer sees that a revoke of one path leaves the key.
*/
const accessReviewAppGrantSchema = z.object({
	permissionKey: z.string().min(1),
	sources: z.array(accessReviewGrantSourceSchema).min(1)
});
/**
* One app that one person can see. Every field is present; a field that can be empty is `null`.
*
* - `appId`, `appSlug`, `displayName`: the app.
* - `access`: the arm that kept the app, {@link ACCESS_ARMS}.
* - `lastOpenAt`: the newest open of the app by the person within the retention of
*   `auth.app_open_days`, an ISO-8601 instant in the `Z` form, or `null` when none is stored.
* - `opens30d`: the opens of the last {@link ACCESS_REVIEW_OPENS_WINDOW_DAYS} UTC days, from 0.
* - `channel`, `version`: the release channel and the app version of the last open, or `null`
*   when the person has no stored open.
* - `unused`: `true` when the person has no open of the app in the last
*   {@link ACCESS_REVIEW_UNUSED_WINDOW_DAYS} days, `false` when the person has one, `null` while
*   that much history does not cover both the person and the org.
* - `grants`: the app permission keys of the app that a grant row gives the person, one entry
*   per key in `permissionKey` order, each with its sources ({@link accessReviewAppGrantSchema};
*   2026-10-29-app-permission-subjects, D-14). A key that the `all` catalog policy alone gives has
*   no entry, and `access` (`owner` or `all-apps-role`) names that reason (R-006). It is the one
*   optional field: a core-api with that
*   feature sends it on every entry, an empty list when the person holds no key of the app, and an
*   older core-api leaves it out.
*/
const accessReviewPersonAppSchema = z.object({
	appId: z.uuid(),
	appSlug: z.string().min(1),
	displayName: z.string(),
	access: accessArmSchema,
	lastOpenAt: z.iso.datetime().nullable(),
	opens30d: z.number().int().min(0),
	channel: z.string().nullable(),
	version: z.string().nullable(),
	unused: z.boolean().nullable(),
	grants: z.array(accessReviewAppGrantSchema).optional()
});
/**
* One active account of the org. Every field is present; a field that can be empty is `null`.
* Each time is an ISO-8601 instant in the `Z` form.
*
* - `userId`, `email`, `displayName`: the account. `displayName` is `null` when the account has none.
* - `roles`: the built-in slug, the direct roles and the roles of the flattened groups,
*   de-duplicated.
* - `createdAt`: when the account was made.
* - `lastSignInAt`: `users.last_login_at`, which moves only on a credential sign-in.
* - `lastSessionAt`: the newest `created_at` of the person's `user` sessions, an on-behalf-of mint
*   left out. A token refresh moves it.
* - `lastAppOpenAt`: the newest stored open of the person, of any app, within the retention. It
*   counts an app the person cannot see now, and every app when the query names `appId`. So it can
*   be newer than every `apps[].lastOpenAt`.
* - `lastActivityAt`: the newest of the three times above it.
* - `dormant`: one of {@link ACCESS_REVIEW_DORMANT_DAYS}, or `null` when the person is not dormant.
* - `apps`: one entry per app the person can see, `default-shell` left out.
*
* Level 2, the reads of user records by this person (S2; R-004). core-api records one read in
* `auth.user_record_view_days` each time a read of one user record by this person answers 200.
* Two paths make that read: `GET /v1/admin/users/{id}` and the MCP tool `get_user` (R-012).
* core-api keeps the rows for the retention setting of R-001. A read of the person's own record
* counts.
*
* - `lastRecordViewAt`: the newest read by this person of any user record, within the retention,
*   or `null` when none is stored.
* - `recordViews30d`: the reads by this person in the last {@link ACCESS_REVIEW_OPENS_WINDOW_DAYS}
*   UTC days, today included, from 0.
* - `recordsViewed30d`: the distinct user records this person read in the same window, from 0. It
*   is at most `recordViews30d`.
*
* `lastActivityAt` and `dormant` read only `createdAt`, `lastSignInAt`, `lastSessionAt` and
* `lastAppOpenAt`.
*/
const accessReviewPersonSchema = z.object({
	userId: z.uuid(),
	email: z.string(),
	displayName: z.string().nullable(),
	roles: z.array(z.string().min(1)),
	createdAt: z.iso.datetime().nullable(),
	lastSignInAt: z.iso.datetime().nullable(),
	lastSessionAt: z.iso.datetime().nullable(),
	lastAppOpenAt: z.iso.datetime().nullable(),
	lastActivityAt: z.iso.datetime().nullable(),
	dormant: z.literal([...ACCESS_REVIEW_DORMANT_DAYS]).nullable(),
	apps: z.array(accessReviewPersonAppSchema),
	lastRecordViewAt: z.iso.datetime().nullable(),
	recordViews30d: z.number().int().min(0),
	recordsViewed30d: z.number().int().min(0)
});
/**
* The 200 body of {@link ACCESS_REVIEW_PEOPLE_ROUTE}.
*
* - `countedSince`: the UTC day (`YYYY-MM-DD`) from which core-api counts opens for this org (D-10).
*   It is the later of two days: the day migration 0080 ran on the DP, and the org's `created_at`
*   day.
* - `unusedComplete`: `true` when {@link ACCESS_REVIEW_UNUSED_WINDOW_DAYS} days of history cover
*   the org, so `unused` can be a boolean; `false` before that.
* - `items`: the page, at most {@link ACCESS_REVIEW_PAGE_SIZE_MAX} people.
* - `nextCursor`: the `cursor` for the next page, or `null` on the last page. The same grammar as
*   the query's `cursor`. A page can hold fewer than `limit` items, or none, with a non-null
*   `nextCursor`: core-api reads at most 100 accounts per request (index default F-16, R-007). A
*   client asks for the next page while `nextCursor` is not `null`.
*/
const accessReviewPeopleResponseSchema = z.object({
	countedSince: z.iso.date(),
	unusedComplete: z.boolean(),
	items: z.array(accessReviewPersonSchema).max(50),
	nextCursor: z.string().min(1).max(PAGE_CURSOR_MAX).regex(/^[A-Za-z0-9_-]+$/).nullable()
});
//#endregion
export { ACCESS_ARMS, ACCESS_REVIEW_DORMANT_DAYS, ACCESS_REVIEW_GRANT_SOURCE_KINDS, ACCESS_REVIEW_GROUP_PATH_MAX, ACCESS_REVIEW_OPENS_WINDOW_DAYS, ACCESS_REVIEW_PAGE_SIZE_DEFAULT, ACCESS_REVIEW_PAGE_SIZE_MAX, ACCESS_REVIEW_PEOPLE_ROUTE, ACCESS_REVIEW_UNUSED_WINDOW_DAYS, ADMIN_ACCESS_REVIEW_PEOPLE_PATH, accessArmSchema, accessReviewAppGrantSchema, accessReviewGrantSourceKindSchema, accessReviewGrantSourceSchema, accessReviewGroupRefSchema, accessReviewPeopleQuerySchema, accessReviewPeopleResponseSchema, accessReviewPersonAppSchema, accessReviewPersonSchema, accessReviewRoutes };

//# sourceMappingURL=access-review.js.map